// Pure helpers for the patient-portal treatment card.
//
// IMPORTANT: this module is intentionally pure — no JSX, no React hooks, no
// window/cookie access — so the treatment-card business rules (fill-request
// selection, refill-count display, edit-lock gating, pending-edit help text) can
// be unit-tested in isolation. These were lifted verbatim out of the
// portalPrescriptionRefills.jsx renderer; the only change is the import site.
//
// See docs/patient-portal-treatment-card.md for the behavior these encode.

import { addDays, isFuture, isPast } from "date-fns";
import pluralize from "pluralize";

import {
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_READY,
  EMR_USER_ID_SYSTEM,
  FILL_PHARMACY_ID_GOGOMEDS,
  FILL_PHARMACY_ID_GOGOMEDS_WEST,
  FILL_PHARMACY_ID_TRUEPILL,
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_INACTIVE,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";

import {
  isPharmacyPickupOrderType,
  numRefillsRemaining,
} from "~/shared/utils/sharedUtils";

// uuid.Nil — the zero value of the patient's lockedByDoctorID UUID column
// ("not locked by anyone").
export const UUID_NIL = "00000000-0000-0000-0000-000000000000";

// Statuses that mean a fill request failed / was withdrawn — never "on the way".
export const FILL_REQUEST_DEAD_STATUSES = [
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_INACTIVE,
];

// Short description of what editing a pending visit can change, tailored to the visit type and
// whether it's a pharmacy-pickup or mail-order fulfillment. Only performance-anxiety (PA/PAF) and
// insomnia-initial (IN) visits show this; GA/GAF and insomnia follow-ups (INF) return "" (no text).
export function pendingEditHelpText(visit) {
  const isPickup = isPharmacyPickupOrderType(visit.orderType);
  let detail = "";
  switch (visit.visitType) {
    case VISIT_TYPE_INSOMNIA:
      detail = isPickup
        ? "Edit your plan, pharmacy, or billing address."
        : "Edit your plan, pharmacy, or shipping address.";
      break;
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      detail = isPickup
        ? "Edit your quantity, pharmacy, billing address, or switch to mail order."
        : "Edit your quantity, pharmacy, shipping speed, or shipping address.";
      break;
    default:
      // GA/GAF and INF (insomnia follow-up): no explanatory text.
      return "";
  }
  return `Want to change something? ${detail}`;
}

// patientLockedByDoctor reports whether the patient is currently locked by a REAL doctor (i.e. a
// clinical hold), as opposed to the transient system lock applied automatically during a
// pending-visit edit. Self-service edits (Edit Pending Visit / Change Pharmacy / Change Shipping
// Address) must be hidden while a real doctor holds the lock.
export function patientLockedByDoctor(patient) {
  const lockedUntil = patient?.lockedUntil;
  if (!lockedUntil || !isFuture(new Date(lockedUntil))) return false;
  // lockedByDoctorID is a non-null UUID column; uuid.Nil means "not locked by anyone".
  const lockedBy = patient?.lockedByDoctorID;
  return (
    !!lockedBy && lockedBy !== UUID_NIL && lockedBy !== EMR_USER_ID_SYSTEM
  );
}

// canEditPharmacy reports whether the patient may still change the pharmacy/shipping address on a
// visit: the visit must be in an editable status and the patient must not be locked by a doctor.
// Mirrors the legacy portal gating.
export function canEditPharmacy(visit, patient) {
  return (
    [
      DOCTOR_VISIT_STATUS_AWAITING_REPLY,
      DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
      DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
      DOCTOR_VISIT_STATUS_PENDING,
      DOCTOR_VISIT_STATUS_READY,
    ].includes(visit.status) && !patientLockedByDoctor(patient)
  );
}

// refillsRemainingText returns the human-readable "X refills remaining" string for a prescription,
// or "" when no refill count should surface (pharmacy pickup, expired/canceled, or a pharmacy that
// requires a follow-up visit).
export function refillsRemainingText(visit, prescription) {
  // Pharmacy-pickup orders don't surface a refill count.
  if (isPharmacyPickupOrderType(visit.orderType)) return "";

  const expired =
    isPast(addDays(new Date(prescription.createdAt), 362)) ||
    prescription.refillsCanceled;
  if (expired) return "";

  let numRefills = numRefillsRemaining(
    prescription.refills,
    prescription.numRefillsFilled,
  );
  // Truepill / GoGoMeds fills require a follow-up visit → show as no refills.
  if (
    prescription.fillPharmacyID == FILL_PHARMACY_ID_TRUEPILL ||
    prescription.fillPharmacyID == FILL_PHARMACY_ID_GOGOMEDS ||
    prescription.fillPharmacyID == FILL_PHARMACY_ID_GOGOMEDS_WEST ||
    prescription.fillPharmacyID == ""
  ) {
    numRefills = 0;
  }

  return numRefills == 0
    ? "No refills remaining"
    : `${pluralize("refill", numRefills, true)} remaining`;
}

// Returns the shipping address to display for a mail-order visit. When a patient
// orders a refill they can pick a DIFFERENT address, which is captured on that
// refill's VisitOrderPayment (VOP) rather than on the visit itself — so prefer
// the address from the most recent VOP that has one. Falls back to the visit's
// own address only when no VOP carries an address (older orders, or before any
// payment row exists). Returns a {address1, address2, city, usState, zip} object.
export function shippingAddressForVisit(visit) {
  const vop = (visit.visitOrderPayments || [])
    .filter((p) => p && p.address1)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const src = vop || visit;
  return {
    address1: src.address1 || "",
    address2: src.address2 || "",
    city: src.city || "",
    usState: src.usState || "",
    zip: src.zip || "",
  };
}

// Formats a {address1, address2, city, state, zip} into display lines:
// "address1 address2" then "City, ST ZIP", skipping any empty piece.
export function addressLines({ address1, address2, city, state, zip }) {
  const lines = [];
  const street = [address1, address2].filter(Boolean).join(" ");
  if (street) lines.push(street);
  const cityState = [state, zip].filter(Boolean).join(" ");
  const cityLine = [city, cityState].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  return lines;
}

// Returns the visit's most recent SHIPPED fill request (one with a trackingURL),
// or null when none is available yet. trackingURL is only populated once a
// shipment goes out, so a non-empty value is itself the "shipment on the way"
// signal; we still skip canceled fill requests. The store returns fill requests
// in no guaranteed order, so pick the newest by createdAt.
export function latestShippedFillRequest(visit) {
  let best = null;
  (visit.fillRequests || []).forEach((fr) => {
    if (!fr || !fr.trackingURL) return;
    if (fr.status === FILL_REQUEST_STATUS_CANCELLED) return;
    if (!best || new Date(fr.createdAt) > new Date(best.createdAt)) {
      best = fr;
    }
  });
  return best;
}

// True when the visit has a mail-order fill request that's been placed and is
// still progressing toward shipment but doesn't have a tracking link yet —
// tracking can lag the fill request by a day or two. This drives the "Tracking
// link coming soon" note for the ORIGINAL order's shipment, mirroring what we
// already show for in-progress refills. Fill requests that already carry a
// tracking link (surfaced via latestShippedFillRequest) or are in a terminal
// failed/cancelled state are excluded.
export function hasFillRequestAwaitingTracking(visit) {
  return (visit.fillRequests || []).some(
    (fr) =>
      fr &&
      !fr.trackingURL &&
      !FILL_REQUEST_DEAD_STATUSES.includes(fr.status),
  );
}

// Go's zero time ("0001-01-01...") marshals to a year <= 1; treat that as "unset".
// A real (e.g. delivered) date has a present-day year.
function isRealDate(value) {
  if (!value) return false;
  const d = new Date(value);
  return !isNaN(d.getTime()) && d.getFullYear() > 1;
}

// Returns the visit's most recent in-progress REFILL fill request, or null.
// Unlike latestShippedFillRequest (which also covers the original order's
// shipment for the "Track shipment" link), this requires the fill request to be
// a refill (isRefill) and not in a failed/cancelled state. It does NOT require a
// trackingURL — a refill is surfaced as soon as it's ordered ("being processed")
// and upgrades to "on the way" once tracking appears (which can lag). A
// failed/cancelled refill (SE/E/ME/X/D/I) is treated as no refill in progress,
// so we don't surface a failed send to the patient. A delivered refill is likewise
// no longer "in progress" — once AfterShip reports delivery (deliveredAt is a real
// date) the "refill on the way" banner clears.
export function latestRefillInProgress(visit) {
  let best = null;
  (visit.fillRequests || []).forEach((fr) => {
    if (!fr || !fr.isRefill) return;
    if (FILL_REQUEST_DEAD_STATUSES.includes(fr.status)) return;
    if (isRealDate(fr.deliveredAt)) return;
    if (!best || new Date(fr.createdAt) > new Date(best.createdAt)) {
      best = fr;
    }
  });
  return best;
}

// Maps a fill request's doctorPrescription{1..4}ID to the visit's prescriptions,
// returning the de-duped medications (by name + form factor) that shipment
// covers. Falls back to the visit's de-duped prescriptions when the fill request
// has no resolvable prescription IDs (older fill requests may not populate them).
export function fillRequestMedications(visit, fr) {
  const ids = [
    fr?.doctorPrescription1ID,
    fr?.doctorPrescription2ID,
    fr?.doctorPrescription3ID,
    fr?.doctorPrescription4ID,
  ].filter((id) => id && id !== UUID_NIL);

  // Resolve against the history list (active + canceled) so a shipment whose
  // prescription was later canceled still shows its medication name. Fall back to
  // the active-only list for payloads that predate allVisitPrescriptions.
  const historyRxs =
    visit.allVisitPrescriptions && visit.allVisitPrescriptions.length
      ? visit.allVisitPrescriptions
      : visit.visitPrescriptions || [];

  const rxById = {};
  historyRxs.forEach((rx) => {
    if (rx) rxById[rx.id] = rx;
  });

  const deDupeByMed = (rxs) => {
    const byMed = {};
    rxs.forEach((rx) => {
      const key = rx.medicationName + rx.medicationFormFactor;
      if (!byMed[key]) byMed[key] = rx;
    });
    return Object.values(byMed);
  };

  const matched = ids.map((id) => rxById[id]).filter(Boolean);
  if (matched.length === 0) return deDupeByMed(historyRxs);
  return deDupeByMed(matched);
}
